import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getSavedPlayerLists } from '../../../actions/saved-lists-actions';

// Rows past this count get a filter box above them; the default list always stays visible.
export const TAIL_FILTER_THRESHOLD = 10;
// Prefix for the synthetic id given to a list that carries neither `id` nor `listId` (see below).
export const NO_ID_PREFIX = 'no-id-';

const normalizeLists = (data) => {
    const rawLists = Array.isArray(data) ? data : (data?.lists || []);
    // Falls back to an index-derived id when a list carries neither an `id` nor a `listId` —
    // leaving this undefined would interpolate the literal string "undefined" into every
    // data-testid, htmlFor/id pairing, and React key for that row, and collapse every such row
    // into one checkbox via `checkedListIds`. Prefixed (not a bare index) so it can never collide
    // with a real numeric/string id already used elsewhere in the same response.
    return rawLists.map((list, index) => ({
        id: list.id ?? list.listId ?? `${NO_ID_PREFIX}${index}`,
        name: list.name ?? list.listName ?? '',
        isDefault: !!(list.isDefault ?? list.isDefaultList),
    }));
};

// Shared list-picker state for the SavedLists modals: loads the member's lists, holds the checked
// set + the new-list name/error, and derives the filtered view. The owning modal keeps its own save
// orchestration (single-player SaveToListModal vs multi-player AddPlayerToListModal) and only reads
// this state — extracted per TMS-2167 so both render the same picker without duplicating the logic.
const useListPicker = ({ initialSavedListIds = [], preCheckDefault = true, enabled = true } = {}) => {
    const dispatch = useDispatch();

    const [lists, setLists] = useState([]);
    const [isLoadingLists, setIsLoadingLists] = useState(enabled);
    const [checkedListIds, setCheckedListIds] = useState(
        () => new Set((initialSavedListIds || []).map(String)),
    );
    const [newListName, setNewListName] = useState('');
    const [nameError, setNameError] = useState(null);
    const [filterText, setFilterText] = useState('');

    useEffect(() => {
        // The single-step Add-Player flow (from List Detail) saves straight into the known list and
        // never renders the picker, so skip the lists fetch entirely rather than firing a GET whose
        // result is discarded.
        if (!enabled) {
            return undefined;
        }
        let isMounted = true;
        dispatch(getSavedPlayerLists())
            .then((result) => {
                if (!isMounted) {
                    return;
                }
                const normalized = normalizeLists(result?.data);
                setLists(normalized);
                const defaultList = normalized.find((list) => list.isDefault);
                // Pre-check the default only for a fresh player with no saves yet, and only when the
                // caller opts in — the multi-add wizard leaves it unchecked so a bulk save never
                // silently adds every selected player to "My Player List". The POST is additive-only,
                // so force-checking it for a player already saved elsewhere can't be undone here.
                const hasExistingSaves = (initialSavedListIds || []).length > 0;
                if (preCheckDefault && defaultList && !hasExistingSaves) {
                    setCheckedListIds((prev) => new Set(prev).add(String(defaultList.id)));
                }
            })
            .catch(() => {
                if (isMounted) {
                    setLists([]);
                }
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoadingLists(false);
                }
            });
        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, enabled]);

    const defaultList = useMemo(() => lists.find((list) => list.isDefault), [lists]);
    const tailLists = useMemo(() => lists.filter((list) => !list.isDefault), [lists]);
    const showFilter = tailLists.length > TAIL_FILTER_THRESHOLD;
    const filteredTailLists = useMemo(() => {
        const trimmedFilter = filterText.trim().toLowerCase();
        if (!trimmedFilter) {
            return tailLists;
        }
        return tailLists.filter((list) => list.name.toLowerCase().includes(trimmedFilter));
    }, [tailLists, filterText]);
    const isFilteredToZero = filterText.trim().length > 0 &&
        tailLists.length > 0 &&
        filteredTailLists.length === 0;

    const trimmedNewListName = newListName.trim();
    // A "no-id-N" fallback row has nothing real to save to on the server — it exists only so the row
    // renders with a stable, unique testid/key. Both the save payload and the enabled/filled gate
    // read the saveable ids, so a synthetic-only selection can't enable Save into an empty POST.
    const saveableListIds = Array.from(checkedListIds).filter((id) => !id.startsWith(NO_ID_PREFIX));
    const isPickerFilled = saveableListIds.length > 0 || trimmedNewListName.length > 0;

    const toggleList = (listId) => {
        setCheckedListIds((prev) => {
            const next = new Set(prev);
            const key = String(listId);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const handleNameChange = (event) => {
        setNewListName(event.target.value);
        setNameError(null);
    };

    const getSaveListIds = () => saveableListIds;

    // The exact controlled props <ListPicker> consumes, pre-shaped so every owning modal renders it
    // as `<ListPicker data-testid={PREFIX} {...picker.listPickerProps} />` — the caller only supplies
    // its own testid prefix, and the ~12-prop forwarding block lives here once instead of per modal.
    const listPickerProps = {
        isLoadingLists,
        defaultList,
        filteredTailLists,
        isFilteredToZero,
        checkedListIds,
        onToggleList: toggleList,
        newListName,
        onNewListNameChange: handleNameChange,
        nameError,
        showFilter,
        filterText,
        onFilterTextChange: (event) => setFilterText(event.target.value),
    };

    return {
        listPickerProps,
        lists,
        isLoadingLists,
        defaultList,
        showFilter,
        filteredTailLists,
        isFilteredToZero,
        checkedListIds,
        toggleList,
        newListName,
        handleNameChange,
        trimmedNewListName,
        isPickerFilled,
        nameError,
        setNameError,
        filterText,
        setFilterText,
        getSaveListIds,
    };
};

export default useListPicker;
